"use client";

import { Fragment } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowLeft, ExternalLink, Store } from "lucide-react";
import { Container } from "@/components/ui/container";
import { cn } from "@/lib/utils";
import { solutionThemeStyles } from "@/lib/solution-styles";
import { findMostSpecificPathMatch } from "./page-sub-nav-utils";

export type PageSubNavTheme =
  | "light"
  | "dark"
  | "blue"
  | "magneto"
  | "yonda"
  | "turnalar";

export interface PageSubNavLink {
  name: string;
  href: string;
  external?: boolean;
  icon?: "store";
}

export interface PageSubNavProps {
  title: string;
  titleHref: string;
  links: readonly PageSubNavLink[];
  cta?: {
    label: string;
    href: string;
    external?: boolean;
  };
  theme?: PageSubNavTheme;
  backHref?: string;
  backLabel?: string;
  breadcrumbs?: readonly PageSubNavLink[];
  onLinkClick?: (href: string) => boolean;
  linkClassName?: string;
  className?: string;
}

export function PageSubNav({
  title,
  titleHref,
  links,
  cta,
  theme = "light",
  backHref,
  backLabel,
  breadcrumbs,
  onLinkClick,
  linkClassName,
  className,
}: PageSubNavProps) {
  const pathname = usePathname();
  const isDark = theme === "dark";
  const isBlue = theme === "blue";
  const isGreen = theme === "magneto";
  const isYonda = theme === "yonda";
  const isTurnalar = theme === "turnalar";
  const turnalarStyles = solutionThemeStyles.turnalar;
  const activeHref = findMostSpecificPathMatch(pathname, links)?.href;

  const activeLinkClasses = isDark
    ? "text-brand-violet"
    : isBlue
      ? "text-brand-blue"
      : isYonda
        ? "text-brand-violet"
        : isTurnalar
          ? turnalarStyles.accentText
          : "text-brand-green";

  const mutedLinkClasses = cn(
    "transition-colors",
    isDark
      ? "text-ink hover:text-ink/80"
      : "text-muted hover:text-ink",
  );

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
    isExternal?: boolean,
  ) => {
    if (isExternal || !onLinkClick) return;
    if (onLinkClick(href)) {
      e.preventDefault();
    }
  };

  return (
    <div
      className={cn(
        "h-14 border-t transition-colors duration-300",
        isDark
          ? "border-line"
          : isYonda
            ? "border-brand-violet-line/70 bg-brand-violet-soft"
            : isTurnalar
              ? cn(turnalarStyles.accentBorder, turnalarStyles.tintBg)
              : isGreen
                ? "border-brand-green-line/70 bg-brand-green-soft"
                : "border-line/50",
        className,
      )}
    >
      <Container className="h-full max-lg:px-0">
        <nav aria-label={`${title} sections`} className="flex items-center h-full overflow-x-auto no-scrollbar px-4 lg:px-0">
          <div className="flex items-center gap-3 w-full min-w-max">
            {backHref && (
              <Link
                href={backHref}
                aria-label={backLabel ?? "Back"}
                className={cn(
                  "shrink-0 inline-flex items-center gap-1 text-sm font-medium transition-colors",
                  isDark
                    ? "text-ink hover:text-ink/80"
                    : "text-muted hover:text-ink",
                )}
              >
                <ArrowLeft className="w-4 h-4" />
              </Link>
            )}

            {breadcrumbs && breadcrumbs.length > 0 ? (
              <div className="flex min-w-0 shrink-0 items-center gap-2">
                {breadcrumbs.map((breadcrumb) => (
                  <Fragment key={breadcrumb.href}>
                    <Link
                      href={breadcrumb.href}
                      className={cn(
                        "max-w-[140px] truncate text-sm font-medium transition-colors",
                        isDark
                          ? "text-ink hover:text-ink/80"
                          : "text-muted hover:text-ink",
                      )}
                    >
                      {breadcrumb.name}
                    </Link>
                    <span
                      className={cn(
                        "text-sm",
                        "text-muted",
                      )}
                    >
                      /
                    </span>
                  </Fragment>
                ))}
                <span
                  className={cn(
                    "max-w-[min(420px,50vw)] truncate font-heading text-sm font-bold",
                    "text-ink",
                  )}
                >
                  {title}
                </span>
              </div>
            ) : backHref && backLabel ? (
              <div className="flex items-center gap-2 shrink-0 min-w-0">
                <Link
                  href={backHref}
                  className={cn(
                    "text-sm font-medium transition-colors truncate max-w-[140px]",
                    isDark
                      ? "text-ink hover:text-ink/80"
                      : "text-muted hover:text-ink",
                  )}
                >
                  {backLabel}
                </Link>
                <span
                  className={cn(
                    "text-sm",
                    "text-muted",
                  )}
                >
                  /
                </span>
                <span
                  className={cn(
                    "font-heading font-bold text-sm truncate max-w-[min(420px,50vw)]",
                    "text-ink",
                  )}
                >
                  {title}
                </span>
              </div>
            ) : (
              /* Title with colored underline highlight */
              <Link
                href={titleHref}
                className={cn(
                  "shrink-0 font-heading font-bold text-sm border-b-[3px] pb-0.5 transition-opacity hover:opacity-80 max-w-[260px] truncate",
                  isDark
                    ? "border-brand-violet"
                    : isBlue
                      ? "border-sky-500"
                      : isYonda
                        ? "border-violet-600"
                        : isTurnalar
                          ? turnalarStyles.accentBorder
                          : "border-primary-600",
                  "text-ink",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  isDark
                    ? "focus-visible:ring-brand-violet"
                    : isYonda
                      ? "focus-visible:ring-violet-600"
                      : isTurnalar
                        ? turnalarStyles.focus
                        : "focus-visible:ring-primary-600",
                )}
              >
                {title}
              </Link>
            )}

            {/* Secondary links + CTA */}
            {(links.length > 0 || cta) && (
              <div
                className={cn(
                  "flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-1 shadow-sm",
                  isDark
                    ? "border-line bg-raised shadow-black/10"
                    : isBlue
                      ? "border-brand-blue-line/80 bg-brand-blue-soft/60 shadow-sky-200/30"
                      : isYonda
                        ? "border-brand-violet-line/80 bg-panel/70 shadow-violet-200/30"
                        : isTurnalar
                          ? cn(turnalarStyles.accentBorder, "bg-panel/70")
                          : isGreen
                            ? "border-brand-green-line/80 bg-panel/70 shadow-primary-200/30"
                            : "border-line/80 bg-panel/70 shadow-stone-200/30",
                )}
              >
                {links.map((link) => {
                  const isActive = activeHref === link.href;

                  return (
                    <Link
                      key={link.name}
                      href={link.href}
                      aria-current={isActive ? "page" : undefined}
                      target={link.external ? "_blank" : undefined}
                      rel={link.external ? "noopener noreferrer" : undefined}
                      onClick={(e) => handleClick(e, link.href, link.external)}
                      className={cn(
                        "px-3 py-1 rounded-full text-xs font-semibold transition-all shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
                        linkClassName,
                        link.icon === "store" &&
                          "inline-flex items-center gap-1",
                        isDark
                          ? "focus-visible:ring-brand-violet"
                          : isYonda
                            ? "focus-visible:ring-violet-600"
                            : isTurnalar
                              ? turnalarStyles.focus
                              : "focus-visible:ring-primary-600",
                        isActive
                          ? cn(
                              activeLinkClasses,
                              isDark
                                ? "bg-raised text-brand-violet"
                                : "bg-panel shadow-sm ring-1",
                              isDark
                                ? ""
                                : isBlue
                                  ? "ring-brand-blue-line/80"
                                  : isYonda
                                    ? "ring-brand-violet-line/80"
                                    : isTurnalar
                                      ? "ring-[#e7cc82]"
                                      : isGreen
                                        ? "ring-brand-green-line/80"
                                        : "ring-line/80",
                            )
                          : cn(
                              mutedLinkClasses,
                              "hover:bg-panel/60",
                              isDark && "hover:bg-raised",
                            ),
                      )}
                    >
                      {link.icon === "store" ? (
                        <>
                          <Store
                            aria-hidden="true"
                            className="h-3 w-3 shrink-0"
                          />
                          <span className="min-w-0 truncate">{link.name}</span>
                        </>
                      ) : (
                        link.name
                      )}
                    </Link>
                  );
                })}

                {cta && (
                  <Link
                    href={cta.href}
                    target={cta.external ? "_blank" : undefined}
                    rel={cta.external ? "noopener noreferrer" : undefined}
                    aria-label={
                      cta.external
                        ? `${cta.label} (opens in a new tab)`
                        : undefined
                    }
                    className={cn(
                      "inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-all shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                      "shadow-sm hover:shadow",
                      isDark ? "text-ai-on-action" : "text-white",
                      isDark
                        ? "bg-ai-action hover:opacity-90 focus-visible:ring-brand-violet"
                        : isBlue
                          ? "bg-sky-600 hover:bg-sky-700"
                          : isYonda
                            ? "bg-violet-700 hover:bg-violet-800 focus-visible:ring-violet-700"
                            : isTurnalar
                              ? cn(turnalarStyles.button, turnalarStyles.focus)
                              : "bg-primary-700 hover:bg-primary-800 focus-visible:ring-primary-700",
                    )}
                  >
                    {cta.label}
                    {cta.external && <ExternalLink className="w-3 h-3" />}
                  </Link>
                )}
              </div>
            )}
          </div>
        </nav>
      </Container>
    </div>
  );
}
